import type { AuthEnvironment } from '@obras/shared';
import { useState, type FormEvent } from 'react';

import { AuthLayout } from '../components/AuthLayout.js';
import { Button } from '../components/ui/Button.js';
import { TextField } from '../components/ui/Field.js';
import { FormError } from '../components/ui/Feedback.js';
import { SingleSelect } from '../components/ui/MultiSelect.js';
import { ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

/** Ambiente pré-escolhido ao trocar de órgão sem sair do sistema. */
function environmentFromUrl(): string | undefined {
  return new URLSearchParams(window.location.search).get('ambiente') ?? undefined;
}

export function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /** Preenchida quando o servidor pede a escolha do ambiente. */
  const [environments, setEnvironments] = useState<readonly AuthEnvironment[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);

  async function attempt(environmentId?: string): Promise<void> {
    setSubmitting(true);
    setFormError(null);
    setFieldErrors({});

    try {
      const choices = await login({ email, password, environmentId });

      // Havendo mais de um ambiente, o servidor devolve a lista em vez da sessão.
      if (choices) {
        setEnvironments(choices);
        setChosen((current) => current ?? choices[0]?.id ?? null);
      }
      // A navegação é consequência do estado de sessão, tratada pelo roteador.
    } catch (error) {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors());
        setFormError(error.details ? null : error.message);
      } else {
        setFormError('Não foi possível conectar ao servidor. Verifique sua conexão.');
      }
      setEnvironments(null);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await attempt(environmentFromUrl());
  }

  if (environments) {
    return (
      <AuthLayout
        title="Onde você quer entrar?"
        description="Sua conta tem acesso a mais de um ambiente."
        footer={
          <button
            type="button"
            onClick={() => setEnvironments(null)}
            className="font-medium text-primary-strong transition-colors hover:text-primary"
          >
            Entrar com outra conta
          </button>
        }
      >
        <div className="flex flex-col gap-4">
          <FormError>{formError}</FormError>

          {/*
            Campo de escolha com busca, e não uma lista de botões: com muitas
            organizações a lista viraria uma parede, e digitar o nome é mais
            rápido que caçar visualmente.
          */}
          <SingleSelect
            label="Ambiente"
            placeholder="Selecione o ambiente"
            options={environments.map((environment) => ({
              value: environment.id,
              label: environment.name,
              detail:
                environment.kind === 'platform' ? 'Administração da plataforma' : 'Painel do órgão',
            }))}
            value={chosen}
            onChange={setChosen}
            searchable={environments.length > 6}
          />

          <Button
            variant="primary"
            size="lg"
            disabled={!chosen}
            loading={submitting}
            onClick={() => chosen && void attempt(chosen)}
          >
            Entrar
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Entrar"
      /* Sem descrição: o painel ao lado já diz o que é o sistema, e no celular
         o slogan aparece junto da marca, logo acima. */
      footer={
        <>
          Esqueceu a senha?{' '}
          <a
            href="/recuperar-senha"
            className="font-medium text-primary-strong transition-colors hover:text-primary"
          >
            Recuperar acesso
          </a>
        </>
      }
    >
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="flex flex-col gap-4"
        noValidate
      >
        <FormError>{formError}</FormError>

        <TextField
          label="E-mail"
          type="email"
          autoComplete="username"
          autoFocus
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          {...(fieldErrors['email'] ? { error: fieldErrors['email'] } : {})}
        />

        <TextField
          label="Senha"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          {...(fieldErrors['password'] ? { error: fieldErrors['password'] } : {})}
        />

        <Button type="submit" variant="primary" size="lg" loading={submitting} className="mt-1">
          Entrar
        </Button>
      </form>
    </AuthLayout>
  );
}
